import { type PluginListenerHandle, registerPlugin } from "@capacitor/core";

export enum TtsEventType {
  IsPlayingUpdated = "is_playing_updated",
  ServiceStoppedUpdated = "service_stopped_updated",
  BookIdUpdated = "book_id_updated",
  ChapterIdUpdated = "chapter_id_updated",
  TitleUpdated = "title_updated",
  IndexUpdated = "index_updated",
}

export interface SearchReplacePattern {
  id: string;
  searchPattern: string;
  replaceValue: string;
  enabled: boolean;
  applyToUi: boolean;
  flags: string;
}

export interface TtsState {
  isPlaying: boolean;
  serviceStopped: boolean;
  bookId: number;
  chapterId: number;
  title: string | null;
  currentIndex: number;
  rate: number;
  pitch: number;
  privacyMode: boolean;
  sentenceDelay: number;
  paragraphDelay: number;
  engine: string | null;
  voice: string | null;
  searchReplacePatterns: SearchReplacePattern[];
  inactivityChapterLookback: number;
}

export interface TtsVoice {
  name: string;
  locale: string;
  label: string;
  language: string;
  region: string;
  displayLanguage: string;
  displayRegion: string;
  isNetwork: string;
}

export interface TtsEngine {
  name: string;
  label: string;
  icon: string;
}

export interface TtsPlugin {
  setApiUrl(options: { apiUrl: string }): Promise<void>;
  setToken(options: { token: string | null }): Promise<void>;
  speak(options: { bookId: number; chapterId: number; index?: number }): Promise<void>;
  stop(): Promise<void>;
  pause(): Promise<void>;
  resume(): Promise<void>;
  nextSentence(): Promise<void>;
  prevSentence(): Promise<void>;
  setRate(options: { rate: number }): Promise<void>;
  setPitch(options: { pitch: number }): Promise<void>;
  setPrivacyMode(options: { enabled: boolean }): Promise<void>;
  setSentenceDelay(options: { delay: number }): Promise<void>;
  setParagraphDelay(options: { delay: number }): Promise<void>;
  setInactivityChapterLookback(options: { lookback: number }): Promise<void>;
  getVoices(): Promise<{ voices: TtsVoice[] }>;
  setVoice(options: { voiceId: string }): Promise<void>;
  getEngines(): Promise<{ engines: TtsEngine[] }>;
  setEngine(options: { engineId: string }): Promise<void>;
  setSearchReplacePatterns(options: { patterns: SearchReplacePattern[] }): Promise<void>;
  getState(): Promise<TtsState>;

  addListener(
    eventName: TtsEventType.IsPlayingUpdated | "is_playing_updated",
    listenerFunc: (data: { value: boolean }) => void,
  ): Promise<PluginListenerHandle> & PluginListenerHandle;
  addListener(
    eventName: TtsEventType.ServiceStoppedUpdated | "service_stopped_updated",
    listenerFunc: (data: { value: boolean }) => void,
  ): Promise<PluginListenerHandle> & PluginListenerHandle;
  addListener(
    eventName: TtsEventType.BookIdUpdated | "book_id_updated",
    listenerFunc: (data: { value: number }) => void,
  ): Promise<PluginListenerHandle> & PluginListenerHandle;
  addListener(
    eventName: TtsEventType.ChapterIdUpdated | "chapter_id_updated",
    listenerFunc: (data: { value: number }) => void,
  ): Promise<PluginListenerHandle> & PluginListenerHandle;
  addListener(
    eventName: TtsEventType.TitleUpdated | "title_updated",
    listenerFunc: (data: { value: string | null }) => void,
  ): Promise<PluginListenerHandle> & PluginListenerHandle;
  addListener(
    eventName: TtsEventType.IndexUpdated | "index_updated",
    listenerFunc: (data: { value: number }) => void,
  ): Promise<PluginListenerHandle> & PluginListenerHandle;
}

const Tts = registerPlugin<TtsPlugin>("Tts");

export default Tts;
